/**
 * Navigation Checklists Library
 *
 * Operational checklists triggered by changing conditions during voyage.
 * Based on WBC3 requirements and COLREGs.
 * Users can customise in Settings.
 */

// ============================================================================
// HEAVY WEATHER CHECKLIST
// ============================================================================
// Trigger: When heavy weather expected or wind >F6
// Regulatory: WBC3 10.6 (Watertight Integrity), 19.2 (Securing of Equipment)

export const HEAVY_WEATHER_CHECKLIST = {
  id: 'heavy_weather',
  name: 'Heavy Weather Preparation',
  category: 'navigation',
  trigger: 'When heavy weather expected or wind >F6',
  regulatory: 'WBC3 10.6, 19.2',
  icon: 'CloudRain',

  fields: [
    {
      id: 'forecast_wind',
      label: 'Forecast Wind (Beaufort)',
      type: 'select',
      required: true,
      options: [
        { value: '6', label: 'F6 - Strong Breeze (22-27 kts)' },
        { value: '7', label: 'F7 - Near Gale (28-33 kts)' },
        { value: '8', label: 'F8 - Gale (34-40 kts)' },
        { value: '9', label: 'F9 - Severe Gale (41-47 kts)' },
        { value: '10+', label: 'F10+ - Storm (48+ kts)' }
      ]
    },
    { id: 'forecast_sea_state', label: 'Forecast Sea State', type: 'text', required: false, placeholder: 'e.g., 3-4m' },
    { id: 'eta_conditions', label: 'ETA to Weather', type: 'text', required: false, placeholder: 'e.g., 2 hours' }
  ],

  items: [
    { id: 'all_hatches_secured', text: 'All hatches and watertight doors secured', required: true },
    { id: 'loose_gear_secured', text: 'All loose gear on deck secured or stowed', required: true },
    { id: 'crew_briefed', text: 'Crew briefed on conditions and watch schedule', required: true },
    { id: 'lifejackets_accessible', text: 'Lifejackets accessible and crew aware', required: true },
    { id: 'navigation_plan', text: 'Consider reduced speed / altered course', required: false },
    { id: 'bilge_alarms_checked', text: 'Bilge alarms checked and operational', required: true },
    { id: 'comms_checked', text: 'VHF and backup communications tested', required: false },
    { id: 'shore_notified', text: 'Shore contact notified of conditions', required: false }
  ]
};

// ============================================================================
// RESTRICTED VISIBILITY CHECKLIST
// ============================================================================
// Trigger: When visibility drops below 2nm (fog, rain, snow)
// Regulatory: COLREGS Rule 19 (Conduct in Restricted Visibility)

export const RESTRICTED_VISIBILITY_CHECKLIST = {
  id: 'restricted_visibility',
  name: 'Restricted Visibility',
  category: 'navigation',
  trigger: 'When visibility < 2nm (fog, rain, snow)',
  regulatory: 'COLREGS Rule 19',
  icon: 'Eye',

  fields: [
    {
      id: 'visibility',
      label: 'Visibility',
      type: 'select',
      required: true,
      options: [
        { value: '1-2nm', label: '1-2 nm (Moderate)' },
        { value: '0.5-1nm', label: '0.5-1 nm (Poor)' },
        { value: '<0.5nm', label: '< 0.5 nm (Very Poor/Fog)' }
      ]
    },
    {
      id: 'cause',
      label: 'Cause',
      type: 'select',
      required: true,
      options: [
        { value: 'fog', label: 'Fog' },
        { value: 'rain', label: 'Heavy Rain' },
        { value: 'snow', label: 'Snow' },
        { value: 'haze', label: 'Haze/Mist' },
        { value: 'spray', label: 'Sea Spray' }
      ]
    }
  ],

  items: [
    { id: 'radar_on', text: 'Radar on and operational', required: true },
    { id: 'safe_speed', text: 'Speed reduced to safe speed for conditions', required: true },
    { id: 'sound_signals', text: 'Fog signals being made (horn/whistle)', required: true },
    { id: 'nav_lights_on', text: 'Navigation lights on', required: true },
    { id: 'lookout_posted', text: 'Extra lookout posted (if crew available)', required: false },
    { id: 'vhf_listening', text: 'VHF Ch 16 monitored continuously', required: true },
    { id: 'engines_ready', text: 'Engines ready for immediate manoeuvre', required: false }
  ]
};

// ============================================================================
// NIGHT NAVIGATION CHECKLIST
// ============================================================================
// Trigger: At sunset / entering darkness
// Regulatory: COLREGS Part C (Lights and Shapes)

export const NIGHT_NAVIGATION_CHECKLIST = {
  id: 'night_navigation',
  name: 'Night Navigation',
  category: 'navigation',
  trigger: 'At sunset / entering darkness',
  regulatory: 'COLREGS Part C',
  icon: 'Moon',

  fields: [
    { id: 'sunset_time', label: 'Sunset Time', type: 'time', required: false },
    { id: 'moon_info', label: 'Moon Phase/Rise', type: 'text', required: false, placeholder: 'e.g., Full moon, rises 22:00' }
  ],

  items: [
    { id: 'nav_lights_tested', text: 'Navigation lights tested and lit', required: true },
    { id: 'bridge_lighting', text: 'Bridge lighting adjusted (red/dim)', required: true },
    { id: 'radar_ready', text: 'Radar on standby or active', required: true },
    { id: 'chart_light', text: 'Chart light / work light available', required: false },
    { id: 'searchlight_ready', text: 'Searchlight ready for use', required: false },
    { id: 'torch_available', text: 'Signalling torch accessible', required: false }
  ]
};

// ============================================================================
// HARBOUR ENTRY CHECKLIST
// ============================================================================
// Trigger: When approaching port / harbour entrance
// Regulatory: WBC3 20 (Navigation), Port Authority requirements

export const HARBOUR_ENTRY_CHECKLIST = {
  id: 'harbour_entry',
  name: 'Harbour Entry',
  category: 'navigation',
  trigger: 'When approaching port / harbour entrance',
  regulatory: 'WBC3 20, Local Port Regulations',
  icon: 'Anchor',

  fields: [
    { id: 'port_name', label: 'Port/Harbour', type: 'text', required: true, placeholder: 'e.g., Southampton' },
    { id: 'berth', label: 'Intended Berth', type: 'text', required: false, placeholder: 'e.g., Berth 42' },
    {
      id: 'pilot_required',
      label: 'Pilot Required',
      type: 'select',
      required: false,
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
        { value: 'exempt', label: 'Exempt' }
      ]
    },
    { id: 'eta', label: 'ETA at Entrance', type: 'time', required: false }
  ],

  items: [
    { id: 'port_vhf_contact', text: 'VHF contact with port control / harbour', required: true },
    { id: 'local_info', text: 'Local information reviewed (tides, hazards)', required: true },
    { id: 'mooring_lines', text: 'Mooring lines prepared', required: true },
    { id: 'fenders_ready', text: 'Fenders rigged', required: true },
    { id: 'anchor_cleared', text: 'Anchor cleared away (emergency use)', required: false },
    { id: 'engine_tested', text: 'Engine response tested (ahead/astern)', required: false },
    { id: 'crew_stations', text: 'Crew at mooring stations', required: true }
  ]
};

// ============================================================================
// ANCHORING CHECKLIST
// ============================================================================
// Trigger: Before/during anchoring operation
// Regulatory: WBC3 20 (Navigation)

export const NAV_ANCHORING_CHECKLIST = {
  id: 'nav_anchoring',
  name: 'Anchoring',
  category: 'navigation',
  trigger: 'Before anchoring / at anchorage',
  regulatory: 'WBC3 20',
  icon: 'Anchor',

  fields: [
    { id: 'anchorage_name', label: 'Anchorage', type: 'text', required: true, placeholder: 'e.g., Cowes Roads' },
    { id: 'depth', label: 'Charted Depth (m)', type: 'number', required: true, placeholder: '0' },
    {
      id: 'bottom_type',
      label: 'Bottom Type',
      type: 'select',
      required: false,
      options: [
        { value: 'sand', label: 'Sand' },
        { value: 'mud', label: 'Mud' },
        { value: 'clay', label: 'Clay' },
        { value: 'rock', label: 'Rock' },
        { value: 'weed', label: 'Weed/Kelp' },
        { value: 'mixed', label: 'Mixed' }
      ]
    },
    { id: 'cable_planned', label: 'Cable to Deploy (m)', type: 'number', required: true, placeholder: '0' },
    {
      id: 'anchor_used',
      label: 'Anchor',
      type: 'select',
      required: false,
      options: [
        { value: 'port', label: 'Port' },
        { value: 'starboard', label: 'Starboard' }
      ]
    }
  ],

  items: [
    { id: 'position_suitable', text: 'Position clear of hazards and traffic', required: true },
    { id: 'swinging_room', text: 'Sufficient swinging room checked', required: true },
    { id: 'anchor_ready', text: 'Anchor ready to let go', required: true },
    { id: 'anchor_ball_shape', text: 'Anchor ball/light ready', required: true },
    { id: 'bearing_logged', text: 'Anchor bearings logged', required: true },
    { id: 'anchor_watch', text: 'Anchor watch arrangements confirmed', required: false },
    { id: 'vhf_listening', text: 'VHF on listening watch', required: false }
  ]
};

// ============================================================================
// WATCH HANDOVER CHECKLIST
// ============================================================================
// Trigger: At each watch change
// Regulatory: WBC3 31 (SMS), STCW-F (for certified crew)

export const WATCH_HANDOVER_CHECKLIST = {
  id: 'watch_handover',
  name: 'Watch Handover',
  category: 'navigation',
  trigger: 'At each watch change',
  regulatory: 'WBC3 31, STCW-F',
  icon: 'Users',

  fields: [
    { id: 'relieving_watchkeeper', label: 'Relieving Watchkeeper', type: 'text', required: true, placeholder: 'Name' },
    { id: 'previous_watchkeeper', label: 'Outgoing Watchkeeper', type: 'text', required: true, placeholder: 'Name' },
    { id: 'watch_time', label: 'Watch Start Time', type: 'time', required: true }
  ],

  items: [
    { id: 'position_confirmed', text: 'Position confirmed and understood', required: true },
    { id: 'course_speed', text: 'Course and speed confirmed', required: true },
    { id: 'traffic_situation', text: 'Traffic situation reviewed', required: true },
    { id: 'weather_conditions', text: 'Weather and sea conditions reviewed', required: true },
    { id: 'standing_orders', text: "Master's standing orders acknowledged", required: false },
    { id: 'nav_warnings', text: 'Nav warnings and hazards briefed', required: false },
    { id: 'equipment_status', text: 'Equipment status confirmed', required: false },
    { id: 'eye_adaptation', text: 'Eyes adapted to conditions (night)', required: false }
  ]
};

// ============================================================================
// EXPORTS
// ============================================================================

export const NAVIGATION_CHECKLISTS = {
  heavy_weather: HEAVY_WEATHER_CHECKLIST,
  restricted_visibility: RESTRICTED_VISIBILITY_CHECKLIST,
  night_navigation: NIGHT_NAVIGATION_CHECKLIST,
  harbour_entry: HARBOUR_ENTRY_CHECKLIST,
  anchoring: NAV_ANCHORING_CHECKLIST,
  watch_handover: WATCH_HANDOVER_CHECKLIST
};

export const NAVIGATION_CHECKLIST_CATEGORIES = {
  NAVIGATION: 'navigation'
};

// For display in UI
export const NAVIGATION_CHECKLIST_LIST = [
  HEAVY_WEATHER_CHECKLIST,
  RESTRICTED_VISIBILITY_CHECKLIST,
  NIGHT_NAVIGATION_CHECKLIST,
  HARBOUR_ENTRY_CHECKLIST,
  NAV_ANCHORING_CHECKLIST,
  WATCH_HANDOVER_CHECKLIST
];
